import { useCallback } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   BOOTH CUES — sound and touch signals for the capture sequence
   ═══════════════════════════════════════════════════════════════════════════

   WHY THIS FILE EXISTS
   A guest who has turned their head away from the glass cannot see the screen.
   Every instruction we give visually is, for that guest, not given at all. The
   frame we get back is a person looking away with a neutral face — the exact
   input that produces a flat portrait. So the countdown has to reach them
   through a channel that does not require them to be looking.

   There are only two such channels on a phone: sound and touch. Neither is
   reliable, and they are unreliable in different ways on each platform, so this
   file is deliberately honest about what it cannot do.

   ⚠️ THE PLATFORM TRUTH, AS OF AUGUST 2026 — READ BEFORE "FIXING" ANYTHING HERE

   VIBRATION
     Android / Chrome ....... navigator.vibrate() works. Requires prior user
                              activation on the page (we have plenty), and
                              silently no-ops if the user has system vibration
                              off or is in Do Not Disturb.
     iOS / Safari ........... THE VIBRATION API HAS NEVER SHIPPED. Not disabled,
                              not behind a flag — never implemented. This is why
                              the vibration we added earlier "never showed up":
                              it was almost certainly tested on an iPhone, where
                              navigator.vibrate is simply undefined and our
                              optional-call swallowed it without a sound.

   THE iOS HAPTIC WORKAROUND, AND ITS CEILING
     Safari 17.4 shipped <input type="checkbox" switch>, a native control that
     fires a system haptic tick when toggled. From 17.4 through 26.4 you could
     fire that programmatically by clicking a hidden one. Apple closed that in
     iOS 26.5. What survives is narrower and worth understanding exactly:

       A DIRECT TAP on a real native switch still produces a haptic.
       SCRIPT can no longer produce one, at all, by any known route.

     So: we CAN give an iPhone real haptic feedback on BUTTON PRESSES, by
     putting an invisible native switch over the button (HapticButton, below).
     We CANNOT give an iPhone a haptic on the countdown, because nobody is
     tapping anything during a countdown. That is not a bug to fix later. It is
     the platform, and the countdown cue for iOS has to be sound and vision.

   SPEECH
     window.speechSynthesis works on both platforms, and is the only cue we can
     fire from script that reaches a guest who is not looking at the screen.
     Its constraints:
       - iOS needs the FIRST speak() to happen inside a real user gesture, or
         everything afterwards is silently dropped. unlockCues() does that on
         the first tap of the wizard.
       - getVoices() is often empty until the voiceschanged event.
       - Safari stops synthesis when the tab is backgrounded and does not
         always recover without a reload.
       - Output follows media volume, and on iPhone the physical ringer switch
         can silence it. AT A FESTIVAL, ASSUME HALF THE PHONES ARE ON SILENT.

   ⚠️ THE DESIGN RULE THAT FOLLOWS FROM ALL OF THAT: sound and touch are
   ENHANCEMENTS. Any sequence that only works when they fire is broken for a
   large share of real guests. The pose choreography has to work in total
   silence on a muted iPhone — which is why the profile shot is now a
   three-quarter turn that keeps the screen in view, rather than a full turn
   away plus a cue to look back. Fix the choreography first; cue it second.

   Everything here is instrumented. cueReport() goes into client_meta on every
   run so the Ledger tells us what actually fired on real festival hardware
   instead of what we hoped would.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─── platform detection ──────────────────────────────────────────────────
   Only ever used to choose which ENHANCEMENT to attempt. Never used to gate
   anything a guest needs, so a wrong answer costs a haptic, not a photo. */

const IS_IOS =
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

/** Feature test for the native switch control, not a version sniff. */
export const hasNativeSwitch = (() => {
  try {
    return typeof HTMLInputElement !== 'undefined' && 'switch' in HTMLInputElement.prototype;
  } catch { return false; }
})();

export const hasVibrate =
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

export const hasSpeech =
  typeof window !== 'undefined' && 'speechSynthesis' in window;

/* ─── accounting ──────────────────────────────────────────────────────────
   Counters, not booleans. "Speech is supported" and "speech was heard" are
   very different claims and only the second one matters. We cannot observe the
   ringer switch, so the closest we get is: we asked N times, N started, N
   finished. A gap between asked and started is the interesting number. */

const tally = { vibeAsk: 0, vibeOk: 0, sayAsk: 0, sayStart: 0, sayEnd: 0, sayErr: 0 };
let unlocked = false;
let lastVibe = null;

/**
 * ⚠️ MINIMUM PERCEPTIBLE PULSE. THIS IS THE MOST LIKELY REASON THE BOOTH'S
 * VIBRATION "NEVER SHOWED UP" ON AN ANDROID PHONE THAT SUPPORTS IT PERFECTLY.
 *
 * Every button in this booth was calling buzz(6) to buzz(16). Those numbers
 * came from nowhere. A modern phone does not have a spinning weight any more —
 * it has a linear resonant actuator, a mass on a spring, and a spring needs
 * time to get moving before it displaces enough air and skin for a finger to
 * register anything. Ten milliseconds is roughly one cycle. The API accepts
 * it, returns true, the hardware genuinely does something, and no human being
 * feels it.
 *
 * navigator.vibrate returning true therefore means "the request was well
 * formed", NOT "the user felt that". Nothing in the platform will ever tell us
 * the difference, which is exactly why this was invisible.
 *
 * 35ms is a conservative floor. The Ledger has a duration ladder to replace
 * this guess with a measurement on the actual booth hardware — do that before
 * the festival rather than trusting this number.
 */
const VIBE_FLOOR = 35;

const atLeastFelt = (pattern) => (
  Array.isArray(pattern)
    /* Even indices are ON, odd are the silent gaps. Only lift the ON ones —
       stretching the gaps would just slow the pattern down. */
    ? pattern.map((ms, i) => (i % 2 === 0 ? Math.max(ms, VIBE_FLOOR) : ms))
    : Math.max(pattern, VIBE_FLOOR)
);

/**
 * Vibrate. Pattern is a duration in ms or an on/off array.
 *
 * ⚠️ Silently absent on iOS forever — the Vibration API has never shipped
 * there. On Android it can also do nothing while reporting success: Silent
 * mode and Do Not Disturb both suppress it at the OS level, as does turning
 * vibration off in system settings. None of that is observable from a web
 * page. If the booth is not buzzing, check the phone before the code.
 */
export const buzz = (pattern = 10) => {
  if (!hasVibrate) { lastVibe = { ok: false, why: 'no-api' }; return false; }
  tally.vibeAsk += 1;
  const felt = atLeastFelt(pattern);
  try {
    const ok = navigator.vibrate(felt);
    if (ok) tally.vibeOk += 1;
    lastVibe = { ok, why: ok ? 'accepted' : 'rejected', pattern: felt };
    return ok;
  } catch (e) {
    lastVibe = { ok: false, why: `threw: ${e?.message || e}` };
    return false;
  }
};

/**
 * Fire one raw pulse and report everything observable about the attempt.
 * Used by the Ledger's cue test, which exists because "it does not vibrate"
 * has at least five unrelated causes and guessing between them from a
 * different continent is not a debugging strategy.
 */
export function vibeProbe(ms) {
  const out = {
    ms,
    has_api: hasVibrate,
    /* Sticky activation is required. If this is false the call is dropped
       whatever else is true — but by the time anyone taps a test button it
       should always be true, so a false here is a genuine finding. */
    activated: typeof navigator !== 'undefined' && navigator.userActivation
      ? navigator.userActivation.hasBeenActive
      : null,
    visible: typeof document !== 'undefined' ? document.visibilityState : null,
    returned: null,
  };
  if (!hasVibrate) return out;
  try { out.returned = navigator.vibrate(ms); } catch (e) { out.returned = `threw: ${e?.message || e}`; }
  return out;
}

/**
 * ⚠️ MUST BE CALLED FROM INSIDE A REAL USER GESTURE (a click/tap handler), or
 * iOS drops every later utterance without an error. We call it on the welcome
 * screen's first tap and again on the capture button — calling twice is free.
 *
 * The unlock utterance is a single space at zero volume: audible to nothing,
 * but it counts as the gesture-initiated speak() that iOS requires.
 */
export function unlockCues() {
  if (!hasSpeech || unlocked) return unlocked;
  try {
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    window.speechSynthesis.speak(u);
    /* Touch getVoices() here too: on several browsers the list is empty until
       something has asked for it once, and the ask is what triggers the load. */
    window.speechSynthesis.getVoices();
    unlocked = true;
  } catch { unlocked = false; }
  return unlocked;
}

/**
 * Say something out loud. Fire-and-forget — never awaited, because a blocked
 * or silenced synthesiser must not be able to stall the countdown.
 *
 * ⚠️ cancel() before speak() is deliberate. Without it a slow utterance queues
 * behind the previous one and the guest hears "three" while the shutter fires.
 * Safari has a long-standing bug where speak() immediately after cancel() can
 * be dropped; that is why sayStart is counted separately from sayAsk. If the
 * Ledger shows asked >> started on iPhones, this is the line to revisit.
 */
export function say(text, { rate = 1, pitch = 1 } = {}) {
  if (!hasSpeech || !text) return false;
  tally.sayAsk += 1;
  try {
    const synth = window.speechSynthesis;
    if (synth.speaking || synth.pending) synth.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = rate;
    u.pitch = pitch;
    u.volume = 1;
    u.onstart = () => { tally.sayStart += 1; };
    u.onend = () => { tally.sayEnd += 1; };
    u.onerror = () => { tally.sayErr += 1; };
    synth.speak(u);
    return true;
  } catch { tally.sayErr += 1; return false; }
}

/**
 * Speak, and report back what the synthesiser actually did.
 *
 * ⚠️ THIS DISTINGUISHES THE TWO FAILURES THAT LOOK IDENTICAL TO A GUEST.
 *
 *   onstart never fires ...... speech is BLOCKED. The unlock did not take, or
 *                              the browser refused. Our bug, and no amount of
 *                              turning the volume up will help.
 *   onstart fires, guest
 *   still hears nothing ...... speech RAN and went out to a muted speaker.
 *                              Their phone, and turning it up will fix it.
 *
 * There is no API anywhere that reports system volume or the ringer switch —
 * the web platform deliberately does not expose it, which is why apps that
 * need it ship a native module. So the second case can only ever be answered
 * by asking a person. This function narrows the question down to the one that
 * is worth asking.
 *
 * Resolves on end, on error, or on timeout — never hangs.
 */
export function sayWatched(text, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!hasSpeech) { resolve({ started: false, why: 'no-api' }); return; }
    let started = false;
    let done = false;
    const finish = (why) => {
      if (done) return;
      done = true;
      resolve({ started, why });
    };
    try {
      const synth = window.speechSynthesis;
      if (synth.speaking || synth.pending) synth.cancel();
      const u = new SpeechSynthesisUtterance(String(text));
      u.volume = 1;
      u.onstart = () => { started = true; tally.sayStart += 1; };
      u.onend = () => { tally.sayEnd += 1; finish('ended'); };
      u.onerror = (e) => { tally.sayErr += 1; finish(`error:${e?.error || 'unknown'}`); };
      tally.sayAsk += 1;
      synth.speak(u);
      /* ⚠️ The timeout is not paranoia. Safari in particular can leave an
         utterance pending forever after a backgrounded tab, firing neither end
         nor error, and a wizard step that waits on that is a dead booth. */
      setTimeout(() => finish('timeout'), timeoutMs);
    } catch (e) {
      resolve({ started: false, why: `threw: ${e?.message || e}` });
    }
  });
}

/** Stop talking immediately. Call on cancel, on unmount, on leaving capture. */
export function hush() {
  if (!hasSpeech) return;
  try { window.speechSynthesis.cancel(); } catch { /* nothing to stop */ }
}

/**
 * What actually happened, for client_meta. Cheap, and it is the only way we
 * will ever learn whether festival phones are hearing us.
 */
export function cueReport() {
  return {
    ios: IS_IOS,
    vibrate_api: hasVibrate,
    native_switch: hasNativeSwitch,
    speech_api: hasSpeech,
    unlocked,
    vibe_floor: VIBE_FLOOR,
    vibe_asked: tally.vibeAsk,
    vibe_accepted: tally.vibeOk,
    vibe_last: lastVibe,
    /* Sticky user activation. If this is ever false at generate() time, the
       vibration calls earlier in the run were dropped by the browser and no
       amount of tuning durations will help. */
    activated: typeof navigator !== 'undefined' && navigator.userActivation
      ? navigator.userActivation.hasBeenActive
      : null,
    say_asked: tally.sayAsk,
    say_started: tally.sayStart,
    say_ended: tally.sayEnd,
    say_errors: tally.sayErr,
  };
}

/**
 * A button that produces a real haptic tick on iPhone.
 *
 * ⚠️ HOW, AND WHY IT LOOKS LIKE THIS. Since iOS 26.5 only a direct tap on a
 * native switch fires a haptic, so the switch itself has to be the thing the
 * finger lands on. We stretch a transparent one across the button and hand it
 * the click. The wood button underneath is purely what you SEE — it is made
 * non-interactive so there is exactly one tap path and no double-firing.
 *
 * ⚠️ It only engages where the native switch exists (Safari 17.4+). Everywhere
 * else — every Android phone, every desktop — this renders the plain button it
 * was given and behaves precisely as it did before. That fallback is the whole
 * safety argument for shipping a hack in the middle of the capture flow: if
 * Apple closes this door too, the booth does not notice.
 *
 * ⚠️ Do not style the input. Removing the control's native appearance also
 * removes the haptic, which is the entire point of it being here.
 */
export function HapticButton({ onPress, label, children, className, style, disabled }) {
  /* ⚠️ `switch` is set through a ref, not as a JSX prop. It is a bare boolean
     attribute that only WebKit knows, and putting it in JSX invites both a
     React unknown-property warning and a lint rule we would have to silence.
     setAttribute is the boring, warning-free way to say the same thing. */
  const attach = useCallback((node) => {
    if (node) node.setAttribute('switch', '');
  }, []);

  if (!hasNativeSwitch || disabled) {
    return (
      <button type="button" className={className} style={style}
              disabled={disabled} onClick={onPress}>
        {children}
      </button>
    );
  }

  return (
    <span style={{ position: 'relative', display: 'block', width: '100%' }}>
      <button type="button" className={className} style={{ ...style, pointerEvents: 'none' }}
              tabIndex={-1} aria-hidden="true">
        {children}
      </button>
      <input
        ref={attach}
        type="checkbox"
        aria-label={label}
        onChange={onPress}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          margin: 0, padding: 0, opacity: 0, cursor: 'pointer',
        }}
      />
    </span>
  );
}
